/**
 * Closed-loop referrals (TRUS-757) — the typed client for the service-referral
 * system that replaces the legacy `/api/referrals` + `/api/providers` flow.
 *
 * Contract: CLOSED_LOOP_REFERRALS.md (items 5–15). Paths and field names are
 * fixed by that document; this module is the only place the frontend spells
 * them.
 *
 * Two halves:
 *  - Staff calls (`closedLoopReferralsAPI`) go through the shared `apiClient`,
 *    which carries the session and the active org (X-CBO-ID).
 *  - Provider calls (`providerResponseAPI`) are the public, token-authenticated
 *    response page (/referral/respond?token=…). They use plain `fetch` with no
 *    session, like the meeting guest page: the link token is the only
 *    credential, and the staff client's 401 handling must never run there.
 */

import { apiClient } from '../apiClient';
import { initializeActiveCboId } from '../../utils/auth';
import { config as productConfig } from '@/src/config';

// ============================================================================
// SHARED TYPES
// ============================================================================

export interface ProviderAddress {
  line1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

/** Item 7: every match-returning endpoint returns this shape. */
export interface ClosedLoopMatch {
  id: number;
  provider_type: 'cbo' | 'provider_directory' | 'manual' | string;
  provider_id: string | null;
  provider_name: string | null;
  provider_email: string | null;
  provider_phone: string | null;
  provider_fax: string | null;
  provider_npi: string | null;
  provider_address: ProviderAddress | null;
  match_score: number | null;
  distance_miles: number | null;
  is_selected: boolean;
  /** The API reports `notified` as `pending`. */
  response_status: 'pending' | 'accepted' | 'declined' | string;
  notified_at: string | null;
  responded_at: string | null;
  notification_method: string | null;
  on_platform: boolean;
}

/** Item 5. */
export interface ProviderSearchResult {
  source: 'network' | 'directory';
  id: string | number;
  name: string;
  npi?: string | null;
  taxonomy?: string | null;
  address?: ProviderAddress | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  distance_miles?: number | null;
  accepts_medi_cal?: boolean | null;
}

export interface ProviderSearchParams {
  q?: string;
  taxonomy?: string;
  zip?: string;
  lat?: number;
  lng?: number;
  radius_miles?: number;
  limit?: number;
}

export interface ProviderSearchResponse {
  results: ProviderSearchResult[];
  /** The server sends `X-Directory-Unavailable: 1` when only network orgs came back. */
  directoryUnavailable: boolean;
}

/** Item 6. */
export interface ManualMatchRequest {
  source: 'network' | 'directory' | 'manual';
  id?: string | number;
  name: string;
  npi?: string;
  phone?: string;
  fax?: string;
  email?: string;
  address?: ProviderAddress;
  notes?: string;
}

/** Item 10. */
export interface MemberContextField {
  name: string;
  label: string;
  value: unknown;
  section?: string | null;
}

export interface MemberContext {
  member_id: number;
  fields: MemberContextField[];
  health_plan: { payer_id: string | null; payer_name: string | null; member_plan_id: string | null } | null;
  medi_cal_id: string | null;
  program_enrollments: { program_id: number; program_name: string }[];
}

/** Item 9 (+ the optional `start_workflow` flag from item 23). */
export type ClosedLoopUrgency = 'routine' | 'urgent' | 'emergent';

export interface CreateServiceReferralRequest {
  member_id: number;
  service_type_code: string;
  urgency_level: ClosedLoopUrgency;
  service_description: string;
  clinical_notes?: string;
  sdoh_needs?: string[];
  auto_match: boolean;
  start_workflow?: boolean;
  /** TRUS-762: file the referral under this case. */
  case_episode_id?: number;
}

export interface CreatedServiceReferral {
  id: number;
  referral_number: string;
  status: string;
  [key: string]: unknown;
}

/** Item 11. */
export type SendChannel = 'inbox' | 'email' | 'fax' | 'web_link' | 'phone_script';

export interface SendRequest {
  channels: SendChannel[];
  message?: string;
}

export interface SendChannelResult {
  channel: SendChannel;
  success: boolean;
  error?: string | null;
  provider_message_id?: string | null;
  /**
   * Not named in the contract: where the server puts the web link and the call
   * script. Read leniently — see `sendResultLink` / `sendResultScript`.
   */
  url?: string | null;
  link?: string | null;
  web_link?: string | null;
  script?: string | null;
  call_script?: string | null;
}

export interface SendResponse {
  results: SendChannelResult[];
  referral_status: string;
}

/** Item 15. */
export interface TimelineEntry {
  at: string;
  kind: 'status' | 'communication' | 'inbound' | 'workflow';
  title: string;
  detail?: string | null;
  channel?: string | null;
  actor?: string | null;
  inbound_item_id?: number | null;
}

// ============================================================================
// HELPERS
// ============================================================================

/** The copyable link from a `web_link` result, whichever field the server used. */
export function sendResultLink(r: SendChannelResult): string | null {
  return r.url || r.link || r.web_link || null;
}

/** The call script from a `phone_script` result, whichever field the server used. */
export function sendResultScript(r: SendChannelResult): string | null {
  return r.script || r.call_script || null;
}

/** Which channels a match can use (F2). Inbox is for on-platform orgs only. */
export function availableChannels(match: Pick<ClosedLoopMatch, 'on_platform' | 'provider_email' | 'provider_fax'>): Record<SendChannel, boolean> {
  return {
    inbox: !!match.on_platform,
    email: !!(match.provider_email && match.provider_email.trim()),
    fax: !!(match.provider_fax && match.provider_fax.trim()),
    web_link: true,
    phone_script: true,
  };
}

export function formatAddress(a: ProviderAddress | null | undefined): string {
  if (!a) return '';
  const cityState = [a.city, a.state].filter(Boolean).join(', ');
  return [a.line1, [cityState, a.zip].filter(Boolean).join(' ')].filter((p) => p && String(p).trim()).join(', ');
}

function dropEmpty<T extends Record<string, unknown>>(params: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

// ============================================================================
// STAFF API (session + active org)
// ============================================================================

const BASE = '/api/service-referrals';

export const closedLoopReferralsAPI = {
  /** Item 9. */
  async createReferral(data: CreateServiceReferralRequest): Promise<CreatedServiceReferral> {
    await initializeActiveCboId();
    const res = await apiClient.post<CreatedServiceReferral>(BASE, data);
    return res.data;
  },

  /** Item 10. */
  async getMemberContext(memberId: number): Promise<MemberContext> {
    await initializeActiveCboId();
    const res = await apiClient.get<MemberContext>(`${BASE}/member-context/${memberId}`);
    return res.data;
  },

  /** Item 5. */
  async searchProviders(params: ProviderSearchParams): Promise<ProviderSearchResponse> {
    await initializeActiveCboId();
    const res = await apiClient.get<ProviderSearchResult[]>(`${BASE}/provider-search`, {
      params: dropEmpty(params as Record<string, unknown>),
    });
    const headers = (res.headers || {}) as Record<string, unknown>;
    const flag = headers['x-directory-unavailable'] ?? headers['X-Directory-Unavailable'];
    return {
      results: Array.isArray(res.data) ? res.data : [],
      directoryUnavailable: String(flag ?? '') === '1',
    };
  },

  /** Item 6. */
  async addManualMatch(referralId: number, data: ManualMatchRequest): Promise<ClosedLoopMatch> {
    await initializeActiveCboId();
    const res = await apiClient.post<ClosedLoopMatch>(`${BASE}/${referralId}/matches/manual`, data);
    return res.data;
  },

  /** Item 7. */
  async getMatches(referralId: number): Promise<ClosedLoopMatch[]> {
    await initializeActiveCboId();
    const res = await apiClient.get<ClosedLoopMatch[]>(`${BASE}/${referralId}/matches`);
    return Array.isArray(res.data) ? res.data : [];
  },

  /**
   * Run auto-match, then read the matches back in the item-7 shape (the
   * auto-match response itself is a run summary, not a match list).
   */
  async autoMatch(referralId: number, maxProviders = 4): Promise<ClosedLoopMatch[]> {
    await initializeActiveCboId();
    await apiClient.post(`${BASE}/${referralId}/auto-match`, null, { params: { max_providers: maxProviders } });
    return closedLoopReferralsAPI.getMatches(referralId);
  },

  /** Item 11. */
  async sendToMatch(referralId: number, matchId: number, data: SendRequest): Promise<SendResponse> {
    await initializeActiveCboId();
    const res = await apiClient.post<SendResponse>(`${BASE}/${referralId}/matches/${matchId}/send`, data);
    return res.data;
  },

  /** Item 15 (newest first). */
  async getTimeline(referralId: number): Promise<TimelineEntry[]> {
    await initializeActiveCboId();
    const res = await apiClient.get<TimelineEntry[]>(`${BASE}/${referralId}/timeline`);
    return Array.isArray(res.data) ? res.data : [];
  },
};

// ============================================================================
// PROVIDER RESPONSE PAGE (token, no login) — item 14
// ============================================================================

export type ProviderAction = 'accept' | 'decline' | 'schedule' | 'seen' | 'outcome';

export const OUTCOME_STATUSES = ['completed', 'no_show', 'declined_service', 'referred_elsewhere', 'other'] as const;
export type OutcomeStatus = (typeof OUTCOME_STATUSES)[number];

export interface TokenReferralSummary {
  valid?: boolean;
  referral_id?: number;
  referral_number?: string | null;
  referral_status?: string | null;
  provider_response_status?: string | null;
  cbo_name?: string | null;
  service_type_code?: string | null;
  service_type_name?: string | null;
  service_description?: string | null;
  urgency_level?: string | null;
  expires_at?: string | null;
  acceptance_deadline?: string | null;
  /**
   * First name + last initial only (item 14). The field name is not fixed by
   * the contract, so the page reads any of these.
   */
  member_display_name?: string | null;
  member_name?: string | null;
  member_first_name?: string | null;
  member_last_initial?: string | null;
  allowed_actions?: ProviderAction[] | string[];
}

export class ProviderResponseError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ProviderResponseError';
    this.status = status;
  }
}

function apiBase(): string {
  const base = typeof window !== 'undefined' ? productConfig.weaveinApi?.baseUrl ?? '' : '';
  return String(base).trim().replace(/\/$/, '');
}

async function tokenRequest<T>(method: 'GET' | 'POST', path: string, token: string, body?: Record<string, unknown>): Promise<T> {
  // The existing token routes take ?token=; the new ones read it from the body.
  // Send both: FastAPI ignores the one a route does not declare.
  const url = `${apiBase()}${BASE}/token/${path}?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: method === 'POST' ? JSON.stringify({ token, ...(body || {}) }) : undefined,
  });
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const detail = (data as { detail?: unknown } | null)?.detail;
    throw new ProviderResponseError(typeof detail === 'string' ? detail : '', res.status);
  }
  return data as T;
}

export const providerResponseAPI = {
  verify(token: string): Promise<TokenReferralSummary> {
    return tokenRequest<TokenReferralSummary>('GET', 'verify', token);
  },
  accept(token: string, notes?: string): Promise<unknown> {
    return tokenRequest('POST', 'accept', token, dropEmpty({ notes }));
  },
  decline(token: string, reason: string, notes?: string): Promise<unknown> {
    return tokenRequest('POST', 'decline', token, dropEmpty({ reason, notes }));
  },
  schedule(token: string, data: { appointment_at: string; location?: string; notes?: string }): Promise<unknown> {
    return tokenRequest('POST', 'schedule', token, dropEmpty(data));
  },
  seen(token: string, seenAt?: string): Promise<unknown> {
    return tokenRequest('POST', 'seen', token, dropEmpty({ seen_at: seenAt }));
  },
  outcome(token: string, outcomeStatus: OutcomeStatus, notes?: string): Promise<unknown> {
    return tokenRequest('POST', 'outcome', token, dropEmpty({ outcome_status: outcomeStatus, notes }));
  },
};

/** The member's display name on the provider page: never more than first name + last initial. */
export function tokenMemberDisplay(s: TokenReferralSummary): string | null {
  if (s.member_display_name) return s.member_display_name;
  if (s.member_first_name) {
    return [s.member_first_name, s.member_last_initial ? `${String(s.member_last_initial).charAt(0)}.` : '']
      .filter(Boolean)
      .join(' ');
  }
  return s.member_name || null;
}

export default closedLoopReferralsAPI;
